import { memo, useCallback, useMemo, useState } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { OrderItemSize, ProductOrderItem } from 'types/api/orders.type';
import { PriceType } from 'types/api/pricing.type';

import { useAppSelector } from 'redux/hooks';

import { usePriceVisible } from 'hooks/usePriceVisible';

import { formatOrderPrice, parsePrice } from 'helpers/parsePrice';
import { getResolvedPriceByType } from 'helpers/pricing';

import { routes } from 'data/routes';

import { ColorImage } from 'components/ColorImage';
import { SizeInput } from 'components/SizeInput';

import { finalisePathToDetailsData } from '../../utils/finalisePathToDetailsData';
import * as S from './ColourRow.styles';

type ColourRowProps = {
  item: ProductOrderItem;
  isSend: boolean;
  control: Control;
  selectedPriceType?: PriceType;
  onQuantityBlur: (
    item: ProductOrderItem,
    size: string,
    quantity: number,
    inputName?: string
  ) => Promise<void>;
  onDeleteColour: (item: ProductOrderItem) => void;
};

function ColourRowComponent(props: ColourRowProps): React.ReactElement {
  const { item, isSend, control, selectedPriceType, onQuantityBlur, onDeleteColour } = props;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const settings = useAppSelector((state) => state.settings);
  const priceVisible = usePriceVisible();
  const [isNavigating, setIsNavigating] = useState(false);

  const colourCode = (item.productColorCode ?? item.currentColor ?? '').trim();
  const colourName =
    i18n.language === 'it' ? item.productColorItDesc : item.productColorEnDesc;
  // The API returns sizes already pre-sorted by `index`. Filter out any with
  // an empty/blank label as a safety net.
  const sizes: OrderItemSize[] = (item.sizes ?? []).filter((s) => s.size && s.size !== '-');

  const totalQuantity = item.totalQuantity ?? 0;

  // Use backend-provided resolvedTotalProductPrice when available; otherwise
  // calculate from per-size resolvedPrices.
  const totalCost = useMemo(() => {
    // When the selected price type matches the backend default (Wholesale) or
    // no specific type is selected, use the pre-computed resolved total.
    const useBackendResolved =
      item.resolvedTotalProductPrice != null &&
      (!selectedPriceType || selectedPriceType === 'WHOLESALE');

    if (useBackendResolved) {
      return parsePrice(item.resolvedTotalProductPrice);
    }

    // Client-side calculation from per-size resolvedPrices
    let total = 0;
    for (const size of sizes) {
      if (size.quantity <= 0) continue; // eslint-disable-line no-continue
      const resolved = getResolvedPriceByType(size.resolvedPrices, selectedPriceType);
      if (resolved && resolved.status === 'FOUND' && resolved.price) {
        total += size.quantity * parsePrice(resolved.price.price);
      }
    }
    return total;
  }, [sizes, selectedPriceType, item.resolvedTotalProductPrice]);

  // Derive currency from resolvedPrices if available
  const currency = useMemo(() => {
    for (const size of sizes) {
      if (size.resolvedPrices && size.resolvedPrices.length > 0) {
        const resolved = getResolvedPriceByType(size.resolvedPrices, selectedPriceType);
        if (resolved?.status === 'FOUND' && resolved.price) {
          return resolved.price.currency;
        }
      }
    }
    return item.currency || '';
  }, [sizes, selectedPriceType, item.currency]);

  /** Sizes are not orderable when no resolved price is FOUND for this size. */
  const isSizePriced = (sq: OrderItemSize) => {
    if (!sq.resolvedPrices || sq.resolvedPrices.length === 0) return false;
    return sq.resolvedPrices.some((rp) => rp.status === 'FOUND' && rp.price);
  };

  const handleSizeBlur = (index: number) => (newValue: number) => {
    // Unchanged values (tabbing through a cell) are skipped by the save queue
    // in useProductTable, which compares against the last queued value rather
    // than the last saved one — an edit reverted before its save went out
    // must still be sent.
    const inputName = `${item.uniqueId}-${sizes[index].size}`;
    return onQuantityBlur(item, sizes[index].size, newValue, inputName);
  };

  const handleNavigateToProduct = useCallback(async () => {
    if (isNavigating) return;
    setIsNavigating(true);
    try {
      const productCode = item.productCode ?? item.itemCode ?? '';
      const pathData = await finalisePathToDetailsData(
        {
          productId: item.productId,
          mainFabricCode: item.mainFabricCode,
          currentColor: colourCode,
          productCode,
          orderNumber: '',
          userAlias: '',
        },
        settings
      );
      navigate(
        routes.productDetails({
          genre: pathData.gender,
          category: pathData.category,
          id: pathData.id,
          itemCode: pathData.itemCode,
          mainFabricCode: pathData.mainFabricCode,
          itemColorCode: pathData.itemColorCode,
          genderCodeAndGenderDesc: pathData.genderCodeAndGenderDesc,
          styleCodeAndStyleDesc: pathData.styleCodeAndStyleDesc,
        })
      );
    } catch {
      // Silently fail — the link simply won't navigate if the API errors.
    } finally {
      setIsNavigating(false);
    }
  }, [colourCode, isNavigating, item, navigate, settings]);

  return (
    <S.Row>
      <S.Wrapper>
        {!isSend && (
          <S.RemoveButton
            data-testid="delete-colour-btn"
            type="button"
            aria-label={t('orderTable.removeColour')}
            onClick={() => onDeleteColour(item)}
          >
            ×
          </S.RemoveButton>
        )}
        <S.SwatchArea
          role="link"
          tabIndex={0}
          aria-label={t('orderTable.viewColourDetails', { colourCode })}
          aria-disabled={isNavigating}
          onClick={handleNavigateToProduct}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleNavigateToProduct();
            }
          }}
        >
          <ColorImage source={item.productColorUrl} alt={colourCode || 'colour'} />
          <S.ColourCodeName>
            <S.ColourCode>{colourCode}</S.ColourCode>
            {colourName ? <S.ColourName>{colourName}</S.ColourName> : null}
          </S.ColourCodeName>
        </S.SwatchArea>
      </S.Wrapper>

      <S.SizesGrid $cols={sizes.length} data-testid="size-grid">
        {sizes.map((sq, idx) => {
          const priced = isSizePriced(sq);
          const resolvedForSize = getResolvedPriceByType(sq.resolvedPrices, selectedPriceType);
          const sizePrice =
            resolvedForSize?.status === 'FOUND' && resolvedForSize.price
              ? resolvedForSize.price.price
              : '';
          return (
            <S.SizeColumn key={`${item.uniqueId}-${sq.size}`}>
              <S.SizeLabel>{sq.size}</S.SizeLabel>
              <SizeInput
                name={`${item.uniqueId}-${sq.size}`}
                control={control}
                sizeQuantity={{
                  size: sq.size,
                  quantity: sq.quantity,
                  minQuantity: sq.minQty,
                  price: sizePrice,
                }}
                disabled={isSend || !priced}
                onBlur={handleSizeBlur(idx)}
              />
            </S.SizeColumn>
          );
        })}
      </S.SizesGrid>

      <S.TotalsCell>
        <S.TotalsLabel>{t('orderTable.totalQty')}</S.TotalsLabel>
        <S.TotalsValue>{totalQuantity}</S.TotalsValue>
      </S.TotalsCell>

      {priceVisible && (
        <S.TotalsCell data-testid="colour-totals">
          <S.TotalsLabel>{t('orderTable.totalCost')}</S.TotalsLabel>
          <S.TotalsValue>{formatOrderPrice(currency, totalCost)}</S.TotalsValue>
        </S.TotalsCell>
      )}
    </S.Row>
  );
}

/** Memoized: a saved quantity re-renders only the row of the item it changed. */
export const ColourRow = memo(ColourRowComponent);
